import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiChevronRight,
  FiExternalLink,
  FiHome,
  FiInbox,
  FiMessageSquare,
  FiSend,
} from "react-icons/fi";

import {
  Messageslist,
  MessageslistId,
  SendRoomMessage,
  uploadPrivate,
  UploadSingleMessageFile,
} from "../Services/messagesService";
import { connectSocket, getSocket } from "./socket/socket";
import { getAdminProfile } from "../Services/userService";
import { avatarColors, RoutesPaths } from "../Router/constant";

const CONTACTS_PER_PAGE = 5;
const MESSAGE_PAGE_OFFSET = 1;
const MESSAGE_PAGE_LIMIT = 20;
const READ_ROOMS_STORAGE_KEY = "partnerMessagesReadRooms";
const ALLOWED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg"];

export default function PartnerMessages() {
  const [contactPage, setContactPage] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [contactTotalPages, setContactTotalPages] = useState(1);
  const [messages, setMessages] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const messagesThreadRef = useRef(null);
  const selectedRoomId = selectedRoom?._id;
  const currentUserId = useMemo(() => getCurrentUserId(), []);
  const query = searchQuery.trim().toLowerCase();

  const filteredRooms = useMemo(() => {
    if (!query) return rooms;

    return rooms.filter((room) => {
      const searchableText = [
        getRoomName(room),
        getOrderRouteId(room),
        getOrderId(room),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [rooms, searchQuery, query]);

  const totalContactPages = contactTotalPages;
  const paginatedContacts = filteredRooms;
  const threadMessages = useMemo(
    () => buildMessageThread(messages, currentUserId),
    [messages, currentUserId],
  );
  const selectedRoomTitle = getRoomTitle(selectedRoom);
  const selectedRoomSubtitle = getRoomSubtitle(selectedRoom);
  const selectedOrderId = getOrderId(selectedRoom);
  const selectedOrderRouteId = getOrderRouteId(selectedRoom);

  const [loading, setLoading] = useState(false);
  const [isUserProfile, setUserProfile] = useState();

  const loadRoomMessages = useCallback(async (roomId) => {
    if (!roomId) return;

    try {
      setLoadingMessages(true);

      const response = await MessageslistId(
        roomId,
        MESSAGE_PAGE_OFFSET,
        MESSAGE_PAGE_LIMIT,
      );

      setMessages(getMessagesData(response));
    } catch (error) {
      console.error("Load room messages failed:", error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const selectRoom = useCallback(
    async (room) => {
      if (!room?._id) return;

      markRoomRead(room);
      setSelectedRoom({ ...room, unreadCount: 0 });
      setSelectedAttachments([]);
      setIsAttachmentMenuOpen(false);
      setRooms((currentRooms) =>
        currentRooms.map((item) =>
          String(item?._id) === String(room._id)
            ? { ...item, unreadCount: 0 }
            : item,
        ),
      );

      await loadRoomMessages(room._id);
      getSocket()?.joinRoom(room._id);
    },
    [loadRoomMessages],
  );

  const loadRooms = useCallback(async () => {
    try {
      setLoadingRooms(true);

      const response = await Messageslist(contactPage, CONTACTS_PER_PAGE);
      const roomData = getRoomsData(response);
      const pagination = getRoomsPagination(response, {
        fallbackPage: contactPage,
        fallbackLimit: CONTACTS_PER_PAGE,
        fallbackCount: roomData.length,
      });

      setRooms(applyReadRooms(roomData));
      setContactTotalPages(pagination.totalPages);
      setMessages([]);
      setSelectedRoom(null);
    } catch (error) {
      console.error("Load conversations failed:", error);
      setRooms([]);
      setContactTotalPages(1);
      setMessages([]);
      setSelectedRoom(null);
    } finally {
      setLoadingRooms(false);
    }
  }, [contactPage]);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await getAdminProfile();
        setUserProfile(res?.data?.data?.user);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  useEffect(() => {
    if (selectedRoomId || rooms.length === 0) return;

    selectRoom(rooms[0]);
  }, [rooms, selectRoom, selectedRoomId]);

  useEffect(() => {
    setContactPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (contactPage <= totalContactPages) return;

    setContactPage(totalContactPages);
  }, [contactPage, totalContactPages]);

  useEffect(() => {
    const socket = connectSocket();

    if (!socket) return undefined;

    const receiveMessage = (message) => {
      const messageRoomId = getMessageRoomId(message);

      if (!messageRoomId) return;

      setRooms((currentRooms) =>
        updateRoomsForMessage(
          currentRooms,
          message,
          messageRoomId,
          selectedRoomId,
        ),
      );

      if (String(messageRoomId) === String(selectedRoomId)) {
        setMessages((currentMessages) =>
          appendMessageIfNew(currentMessages, message),
        );
      }
    };

    socket.on("receive_message", receiveMessage);

    return () => {
      socket.off("receive_message", receiveMessage);
    };
  }, [selectedRoomId]);

  useEffect(() => {
    if (rooms.length === 0) return;

    const socket = getSocket();

    rooms.forEach((room) => {
      if (room?._id) {
        socket?.joinRoom(room._id);
      }
    });
  }, [rooms]);

  useEffect(() => {
    if (!selectedRoomId) return;

    getSocket()?.joinRoom(selectedRoomId);
  }, [selectedRoomId]);

  useEffect(() => {
    const messagesThread = messagesThreadRef.current;

    if (!messagesThread) return;

    requestAnimationFrame(() => {
      messagesThread.scrollTo({
        top: messagesThread.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [threadMessages.length, selectedRoomId, loadingMessages]);

  const handleContactPageChange = (page) => {
    setContactPage(Math.min(Math.max(page, 1), totalContactPages));
  };

  const handleAttachmentChange = async (event, fileKind) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (selectedFiles.length === 0) return;

    try {
      setUploadingAttachment(true);

      const uploadedFiles = [];

      for (const [index, file] of selectedFiles.entries()) {
        if (fileKind === "image" && !isAllowedImageFile(file)) {
          toast.error(
            "Error: Only .png, .jpg, .jpeg, and .pdf files are allowed!",
          );
          continue;
        }

        try {
          const uploadResponse = await UploadSingleMessageFile(file);

          if (uploadResponse?.data?.status === false) {
            toast.error(getUploadErrorMessage(uploadResponse));
            continue;
          }

          uploadedFiles.push({
            id: `${file.name}-${file.size}-${file.lastModified}-${fileKind}-${Date.now()}-${index}`,
            file,
            fileKind,
            uploadedName: getUploadedFileName(uploadResponse, file),
          });
        } catch (error) {
          console.error("Attachment upload failed:", error);
          toast.error(getUploadErrorMessage(error));
        }
      }

      if (uploadedFiles.length > 0) {
        setSelectedAttachments((currentFiles) => [
          ...currentFiles,
          ...uploadedFiles,
        ]);
        setIsAttachmentMenuOpen(false);
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!selectedRoomId || sendingMessage) return;

    const trimmedMessage = messageText.trim();
    const files = selectedAttachments.map((attachment) => attachment.file);

    if (!trimmedMessage && files.length === 0) return;

    try {
      setSendingMessage(true);

      const attachments = selectedAttachments.map(
        (attachment) => attachment.uploadedName || attachment.file?.name,
      );

      const payload = {
        message: trimmedMessage,
        attachments,
      };
      const response = await SendRoomMessage(selectedRoomId, payload);
      const sentMessage =
        getSentMessage(response) ||
        createLocalMessage(selectedRoomId, trimmedMessage, attachments);

      setMessages((currentMessages) =>
        appendMessageIfNew(currentMessages, {
          ...sentMessage,
          isOwn: true,
          roomId: selectedRoomId,
        }),
      );
      setRooms((currentRooms) =>
        updateRoomsForMessage(
          currentRooms,
          sentMessage,
          selectedRoomId,
          selectedRoomId,
          true,
        ),
      );
      setMessageText("");
      setSelectedAttachments([]);
      setIsAttachmentMenuOpen(false);
    } catch (error) {
      console.error("Send message failed:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const getColorFromName = (name = "") => {
    let hash = 0;

    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  const handleView = async (filename) => {
    try {
      const response = await uploadPrivate(filename);

      const blob = response?.data;

      if (!(blob instanceof Blob)) {
        console.error("Invalid file response:", response);
        return;
      }

      const fileUrl = URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("File preview error:", error);
    }
  };

  return (
    <div className="messagesScreen commonHeaderPadding qm-chat qm-chat-partner">
      {/* <div className="qm-header">
        <nav className="qd-breadcrumb">
          <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
            <FiHome />
          </NavLink>
          <FiChevronRight />
          <span className="qd-breadcrumb-current">Messages</span>
        </nav>
        <div className="qm-title-row">
          <span className="qm-title-icon" aria-hidden="true">
            <FiMessageSquare />
          </span>
          <div>
            <h1 className="qm-title">Messages</h1>
            <p className="qm-subtitle">Chat with the Quimera team about your bookings.</p>
          </div>
        </div>
      </div> */}
      <div className="messagesShell">
        <aside className="messagesSidebar" aria-label="Conversation list">
          <div className="messagesSidebarTop">
            <div className="messagesOwner">
              {loading ? (
                <div className="profileLoader">Loading...</div>
              ) : (
                <>
                  <div className="userIconSection">
                    {isUserProfile?.contactPersonName
                      ?.split(" ")
                      .map((word) => word[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </div>

                  <div className="messagesOwnerCopy">
                    <h1>{isUserProfile?.contactPersonName}</h1>
                    {/* <p>{isUserProfile?.role}</p> */}
                  </div>
                </>
              )}
            </div>

            <label className="messagesSearch">
              <SearchIcon />
              <input
                type="search"
                value={searchQuery}
                placeholder="Search..."
                aria-label="Search messages"
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </label>
          </div>

          <div className="messagesContactList">
            {loadingRooms &&
              [...Array(5)].map((_, i) => (
                <div className="qm-contact-skel" key={i} aria-hidden="true">
                  <span className="qd-skel qm-skel-avatar" />
                  <span className="qm-skel-lines">
                    <span className="qd-skel" style={{ width: "60%", height: 12 }} />
                    <span className="qd-skel" style={{ width: "85%", height: 10 }} />
                  </span>
                </div>
              ))}
            {!loadingRooms && paginatedContacts.length === 0 && (
              <div className="messagesEmptyState qm-empty">
                <FiInbox aria-hidden="true" />
                <strong>No conversations found</strong>
                <span>
                  {searchQuery ? "Try a different search." : "New chats will appear here."}
                </span>
              </div>
            )}
            {!loadingRooms &&
              paginatedContacts.map((room) => (
                <button
                  key={room._id}
                  className={`messagesContact ${
                    selectedRoomId === room._id ? "active" : ""
                  }`}
                  type="button"
                  onClick={() => selectRoom(room)}
                >
                  <Avatar
                    initials={getInitials(getRoomName(room))}
                    color={getColorFromName(getRoomName(room))}
                  />
                  <span className="messagesContactCopy">
                    <span className="messagesContactHeader">
                      <div className="getRoomNameOuter">
                        <strong>{getRoomTitle(room)}</strong>
                        {/* <div className="getRoomName messagesPreview">
                          (<span className="">{getRoomName(room)}</span> )
                        </div> */}
                      </div>
                      <span>{getRoomTime(room)}</span>
                    </span>
                    {getOrderId(room) && (
                      <span className="qm-order-tag">#{getOrderId(room)}</span>
                    )}
                    <span className="messagesPreview">
                      {getLastMessagePreview(room)}
                    </span>
                  </span>
                  {room?.unreadCount > 0 && (
                    <span className="messagesUnread">{room.unreadCount}</span>
                  )}
                </button>
              ))}
          </div>

          {!loadingRooms && totalContactPages > 1 && (
            <div
              className="messagesContactPagination"
              aria-label="Contacts pagination"
            >
              <button
                type="button"
                onClick={() => handleContactPageChange(contactPage - 1)}
                disabled={contactPage === 1}
                aria-label="Previous contacts page"
              >
                <ChevronLeftIcon />
              </button>
              <div className="messagesContactPageNumbers">
                {Array.from({ length: totalContactPages }, (_, index) => {
                  const page = index + 1;

                  return (
                    <button
                      className={page === contactPage ? "active" : ""}
                      type="button"
                      key={page}
                      onClick={() => handleContactPageChange(page)}
                      aria-current={page === contactPage ? "page" : undefined}
                      aria-label={`Contacts page ${page}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => handleContactPageChange(contactPage + 1)}
                disabled={contactPage === totalContactPages}
                aria-label="Next contacts page"
              >
                <ChevronRightIcon />
              </button>
            </div>
          )}
        </aside>

        <main className="messagesChatPanel" aria-label="Chat conversation">
          <header className="messagesChatHeader">
            <div className="messagesChatProfile">
              <Avatar
                initials={getInitials(selectedRoomTitle)}
                color="#e74316"
                online={Boolean(selectedRoom?.online)}
                size="large"
              />
              <div>
                <div className="messagesChatName">
                  <h2>{selectedRoomTitle}</h2>
                  {selectedRoom?.online && (
                    <span className="messagesOnlinePill">Online</span>
                  )}
                </div>
                <p>{selectedOrderId}</p>
              </div>
            </div>

            {selectedRoom && !loadingMessages && (
              <>
                {selectedOrderRouteId && (
                  <NavLink
                    to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${selectedOrderRouteId}`}
                    className="qd-btn qd-btn-outline qd-btn-sm qm-order-btn"
                  >
                    <FiExternalLink aria-hidden="true" /> Go to order
                  </NavLink>
                )}
              </>
            )}
          </header>

          <div className="messagesThread" ref={messagesThreadRef}>
            {!selectedRoom && (
              <div className="messagesEmptyState qm-empty qm-empty-large">
                <span className="qm-empty-art">
                  <FiMessageSquare aria-hidden="true" />
                </span>
                <strong>Select a conversation</strong>
                <span>Choose a chat on the left to view messages.</span>
              </div>
            )}
            {selectedRoom && loadingMessages && (
              <div className="messagesEmptyState qm-empty">
                <span className="qm-spinner" aria-hidden="true" />
                <span>Loading messages...</span>
              </div>
            )}
            {selectedRoom &&
              !loadingMessages &&
              threadMessages.length === 0 && (
                <div className="messagesEmptyState qm-empty qm-empty-large">
                  <span className="qm-empty-art">
                    <FiSend aria-hidden="true" />
                  </span>
                  <strong>No messages yet</strong>
                  <span>Send the first message to start the conversation.</span>
                </div>
              )}
            {selectedRoom &&
              !loadingMessages &&
              threadMessages.map((item) => {
                if (item.type === "date") {
                  return (
                    <div className="messagesDateDivider" key={item.id}>
                      <span>{item.label}</span>
                    </div>
                  );
                }

                return (
                  <div
                    className={`messagesBubbleRow ${
                      item.side === "outgoing" ? "outgoing" : "incoming"
                    }`}
                    key={item.id}
                  >
                    {item.type === "file" ? (
                      <div
                        className="messagesBubble messagesFileBubble cursorPointer"
                        onClick={() => handleView(item.fileName)}
                      >
                        <div className="messagesFile">
                          <span className="messagesFileIcon">
                            {item.fileType == "pdf" ? (
                              <PdfIcon />
                            ) : (
                              <ImageIcon />
                            )}
                          </span>
                          <span>
                            <strong>{item.fileName}</strong>
                            <small>
                              {item.fileSize}
                              {item.fileType && <> &middot; {item.fileType}</>}
                            </small>
                          </span>
                        </div>
                        <MessageMeta time={item.time} />
                      </div>
                    ) : (
                      <div className="messagesBubble">
                        <p>{item.text}</p>
                        <MessageMeta
                          time={item.time}
                          light={item.side === "outgoing"}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <form className="messagesComposer" onSubmit={handleSendMessage}>
            <div className="messagesInputBar">
              <span className="messagesAttachmentPicker">
                <button
                  type="button"
                  aria-label={
                    selectedAttachments.length > 0
                      ? `${selectedAttachments.length} attachments selected`
                      : "Attach file"
                  }
                  aria-expanded={isAttachmentMenuOpen}
                  aria-haspopup="menu"
                  disabled={
                    !selectedRoomId || sendingMessage || uploadingAttachment
                  }
                  onClick={() => setIsAttachmentMenuOpen((isOpen) => !isOpen)}
                >
                  <AttachmentIcon />
                </button>
                {selectedAttachments.length > 0 && (
                  <span className="messagesAttachmentCount">
                    {selectedAttachments.length}
                  </span>
                )}
                {isAttachmentMenuOpen && (
                  <>
                    <button
                      className="messagesAttachmentDismissLayer"
                      type="button"
                      aria-label="Close attachment menu"
                      onClick={() => setIsAttachmentMenuOpen(false)}
                    />
                    <div className="messagesAttachmentMenu">
                      <label className="messagesAttachmentMenuItem">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          disabled={uploadingAttachment}
                          onChange={(event) =>
                            handleAttachmentChange(event, "image")
                          }
                        />
                        <span className="messagesAttachmentMenuIcon image">
                          <ImageIcon />
                        </span>
                        <span>Image</span>
                      </label>
                      <label className="messagesAttachmentMenuItem">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          multiple
                          disabled={uploadingAttachment}
                          onChange={(event) =>
                            handleAttachmentChange(event, "document")
                          }
                        />
                        <span className="messagesAttachmentMenuIcon document">
                          <DocumentIcon />
                        </span>
                        <span>Document</span>
                      </label>
                    </div>
                  </>
                )}
              </span>
              <input
                value={messageText}
                placeholder="Send a message..."
                aria-label="Message text"
                disabled={!selectedRoomId || sendingMessage}
                onChange={(event) => setMessageText(event.target.value)}
              />
              <button
                className="messagesSendButton"
                type="submit"
                aria-label={sendingMessage ? "Sending message" : "Send message"}
                disabled={
                  !selectedRoomId ||
                  sendingMessage ||
                  uploadingAttachment ||
                  (!messageText.trim() && selectedAttachments.length === 0)
                }
              >
                <SendIcon />
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

function getRoomsData(response) {
  const data = response?.data?.data ?? response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rooms)) return data.rooms;
  if (Array.isArray(data?.data?.rooms)) return data.data.rooms;

  return [];
}

function applyReadRooms(rooms) {
  const readRooms = getReadRooms();

  return rooms.map((room) =>
    isRoomRead(room, readRooms) ? { ...room, unreadCount: 0 } : room,
  );
}

function markRoomRead(room) {
  if (!room?._id || typeof window === "undefined") return;

  const readRooms = getReadRooms();
  readRooms[String(room._id)] = new Date().toISOString();
  localStorage.setItem(READ_ROOMS_STORAGE_KEY, JSON.stringify(readRooms));
  window.dispatchEvent(new Event("messageRoomsRead"));
}

function getReadRooms() {
  if (typeof window === "undefined") return {};

  try {
    const savedRooms = JSON.parse(
      localStorage.getItem(READ_ROOMS_STORAGE_KEY) || "{}",
    );

    return savedRooms && typeof savedRooms === "object" ? savedRooms : {};
  } catch (error) {
    return {};
  }
}

function isRoomRead(room, readRooms) {
  const readAt = readRooms[String(room?._id || "")];

  if (!readAt) return false;

  const readTime = Date.parse(readAt);
  const lastMessageTime = Date.parse(
    room?.lastMessageAt || room?.updatedAt || room?.createdAt || "",
  );

  if (!Number.isFinite(readTime)) return false;
  if (!Number.isFinite(lastMessageTime)) return true;

  return lastMessageTime <= readTime;
}

function getRoomsPagination(
  response,
  {
    fallbackPage = 1,
    fallbackLimit = CONTACTS_PER_PAGE,
    fallbackCount = 0,
  } = {},
) {
  const data = response?.data?.data ?? response?.data;
  const root = response?.data || {};
  const pagination =
    data?.pagination ||
    data?.paginationData ||
    data?.pageInfo ||
    data?.pageInfoData ||
    data?.meta ||
    root?.pagination ||
    root?.paginationData ||
    root?.pageInfo ||
    root?.pageInfoData ||
    root?.meta ||
    {};
  const currentPage =
    getNumberValue(
      data?.page,
      data?.currentPage,
      data?.current_page,
      pagination?.page,
      pagination?.currentPage,
      pagination?.current_page,
      root?.page,
      root?.currentPage,
      root?.current_page,
    ) || fallbackPage;
  const limit =
    getNumberValue(
      data?.limit,
      data?.pageSize,
      data?.page_size,
      data?.perPage,
      data?.per_page,
      pagination?.limit,
      pagination?.pageSize,
      pagination?.page_size,
      pagination?.perPage,
      pagination?.per_page,
      root?.limit,
      root?.pageSize,
      root?.page_size,
    ) || fallbackLimit;
  const total =
    getNumberValue(
      data?.total,
      data?.count,
      data?.totalCount,
      data?.totalRecords,
      data?.totalRecord,
      data?.totalItems,
      data?.totalDocs,
      data?.totalRooms,
      data?.totalRoom,
      data?.totalConversations,
      data?.recordsTotal,
      pagination?.total,
      pagination?.count,
      pagination?.totalCount,
      pagination?.totalRecords,
      pagination?.totalRecord,
      pagination?.totalItems,
      pagination?.totalDocs,
      pagination?.totalRooms,
      pagination?.totalRoom,
      pagination?.totalConversations,
      pagination?.recordsTotal,
      root?.total,
      root?.count,
      root?.totalCount,
      root?.totalRecords,
      root?.totalRecord,
      root?.totalItems,
      root?.totalDocs,
      root?.totalRooms,
      root?.totalRoom,
      root?.totalConversations,
      root?.recordsTotal,
    ) ?? fallbackCount;
  const hasNextPage = Boolean(
    data?.hasNextPage ||
    data?.hasNext ||
    pagination?.hasNextPage ||
    pagination?.hasNext ||
    root?.hasNextPage ||
    root?.hasNext,
  );
  const fallbackTotalPages =
    total > 0
      ? Math.ceil(total / limit)
      : hasNextPage || fallbackCount === limit
        ? fallbackPage + 1
        : fallbackPage;
  const totalPages =
    getNumberValue(
      data?.totalPages,
      data?.totalPage,
      data?.total_pages,
      data?.total_page,
      data?.pageCount,
      data?.page_count,
      data?.pages,
      data?.lastPage,
      data?.last_page,
      pagination?.totalPages,
      pagination?.totalPage,
      pagination?.total_pages,
      pagination?.total_page,
      pagination?.pageCount,
      pagination?.page_count,
      pagination?.pages,
      pagination?.lastPage,
      pagination?.last_page,
      root?.totalPages,
      root?.totalPage,
      root?.total_pages,
      root?.total_page,
      root?.pageCount,
      root?.page_count,
      root?.pages,
      root?.lastPage,
      root?.last_page,
    ) || fallbackTotalPages;

  return {
    currentPage,
    totalPages: Math.max(1, totalPages),
  };
}

function getNumberValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;

    const numberValue = Number(value);

    if (Number.isFinite(numberValue)) return numberValue;
  }

  return null;
}

function getMessagesData(response) {
  const data = response?.data?.data ?? response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data?.messages)) return data.data.messages;

  return [];
}

function getSentMessage(response) {
  const data = response?.data?.data ?? response?.data;
  const candidates = [
    data?.message,
    data?.chatMessage,
    data?.data?.message,
    data?.data?.chatMessage,
    data,
  ];

  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      (candidate._id || candidate.message || candidate.attachments),
  );
}

function getUploadedFileName(response, file) {
  const data = response?.data?.data ?? response?.data;
  const candidates = [
    data?.fileName,
    data?.filename,
    data?.name,
    data?.url,
    data?.fileUrl,
    data?.path,
    data,
  ];
  const uploadedValue = candidates.find(
    (candidate) => typeof candidate === "string",
  );

  if (!uploadedValue) return file.name;

  return uploadedValue.split("/").pop() || file.name;
}

function getUploadErrorMessage(errorOrResponse) {
  const responseData =
    errorOrResponse?.response?.data?.data ||
    errorOrResponse?.response?.data ||
    errorOrResponse?.data?.data ||
    errorOrResponse?.data;

  return (
    responseData?.message ||
    responseData?.error ||
    errorOrResponse?.message ||
    "File upload failed"
  );
}

function isAllowedImageFile(file) {
  const fileName = String(file?.name || "").toLowerCase();

  return ALLOWED_IMAGE_EXTENSIONS.some((extension) =>
    fileName.endsWith(extension),
  );
}

function createLocalMessage(roomId, message, attachments) {
  return {
    _id: `local-${Date.now()}`,
    roomId,
    message,
    attachments,
    senderRole: "partner",
    createdAt: new Date().toISOString(),
    isOwn: true,
  };
}

function appendMessageIfNew(messages, message) {
  const messageId = getRawMessageId(message);

  if (
    messageId &&
    messages.some((item) => String(getRawMessageId(item)) === String(messageId))
  ) {
    return messages;
  }

  return [...messages, message];
}

function updateRoomsForMessage(
  rooms,
  message,
  roomId,
  selectedRoomId,
  fromSelf = false,
) {
  return rooms.map((room) => {
    if (String(room?._id) !== String(roomId)) return room;

    const isSelectedRoom = String(roomId) === String(selectedRoomId);
    const currentUnreadCount = Number(room?.unreadCount || 0);
    const text = getMessageText(message);
    const attachments = getMessageAttachments(message);

    return {
      ...room,
      lastMessage:
        text || (attachments.length > 0 ? "Attachment" : room.lastMessage),
      lastMessageAt:
        message?.createdAt ||
        message?.sentAt ||
        message?.timestamp ||
        new Date().toISOString(),
      lastMessageBy:
        message?.sender?._id || message?.sender || room.lastMessageBy,
      unreadCount: isSelectedRoom || fromSelf ? 0 : currentUnreadCount + 1,
    };
  });
}

function buildMessageThread(messages, currentUserId) {
  let previousDateLabel = "";

  return messages.reduce((threadItems, message, index) => {
    const dateLabel = getMessageDateLabel(message);

    if (dateLabel && dateLabel !== previousDateLabel) {
      threadItems.push({
        type: "date",
        label: dateLabel,
        id: `date-${dateLabel}-${index}`,
      });
      previousDateLabel = dateLabel;
    }

    threadItems.push(...normalizeMessageItems(message, currentUserId, index));
    return threadItems;
  }, []);
}

function normalizeMessageItems(message, currentUserId, index) {
  const attachments = getMessageAttachments(message);
  const messageType = String(
    message?.messageType || message?.type || "",
  ).toLowerCase();
  const side = getMessageSide(message, currentUserId);
  const time = getMessageTime(message);
  const text = getMessageText(message);
  const items = [];

  if (text) {
    items.push({
      type: "message",
      side,
      text,
      time,
      id: `${getMessageId(message, index)}-text`,
    });
  }

  if (
    attachments.length > 0 ||
    ["file", "image", "document"].includes(messageType)
  ) {
    const files = attachments.length > 0 ? attachments : [message];

    files.forEach((file, fileIndex) => {
      items.push({
        type: "file",
        side,
        fileName: getAttachmentName(file, message),
        fileSize: getAttachmentSize(file, message),
        fileType: getAttachmentType(file, messageType),
        time,
        id: `${getMessageId(message, index)}-file-${fileIndex}`,
      });
    });
  }

  return items.length > 0
    ? items
    : [
        {
          type: "message",
          side,
          text: "",
          time,
          id: getMessageId(message, index),
        },
      ];
}

function getMessageSide(message, currentUserId) {
  if (message?.isOwn || message?.side === "outgoing") return "outgoing";

  const senderId =
    message?.sender?._id ||
    message?.sender?.id ||
    message?.senderId?._id ||
    message?.senderId ||
    message?.userId?._id ||
    message?.userId ||
    message?.createdBy?._id ||
    message?.createdBy;

  if (currentUserId && senderId && String(senderId) === String(currentUserId)) {
    return "outgoing";
  }

  const senderRole = String(
    message?.senderRole || message?.sender?.role || message?.senderModel || "",
  ).toLowerCase();

  return senderRole === "partner" ? "outgoing" : "incoming";
}

function getMessageText(message) {
  if (typeof message === "string") return message;

  return (
    message?.message ||
    message?.text ||
    message?.content ||
    message?.body ||
    message?.caption ||
    ""
  );
}

function getMessageAttachments(message) {
  if (Array.isArray(message?.attachments)) return message.attachments;
  if (Array.isArray(message?.files)) return message.files;
  if (message?.attachment) return [message.attachment];
  if (message?.file) return [message.file];
  if (message?.document) return [message.document];
  if (message?.image) return [message.image];
  if (message?.fileUrl || message?.url) return [message];

  return [];
}

function getAttachmentName(file, message) {
  if (typeof file === "string") return file.split("/").pop() || file;

  const name =
    file?.originalName ||
    file?.fileName ||
    file?.filename ||
    file?.name ||
    message?.fileName ||
    message?.documentName;

  if (name) return name;

  const url = file?.fileUrl || file?.url || message?.fileUrl || message?.url;
  return url ? String(url).split("/").pop() : "Attachment";
}

function getAttachmentSize(file, message) {
  if (typeof file === "string") return "File";

  const size = file?.size || file?.fileSize || message?.fileSize;

  if (!size) return "File";
  if (typeof size === "string") return size;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentType(file, messageType) {
  if (typeof file === "string") {
    return file.includes(".") ? file.split(".").pop() : messageType || "file";
  }

  const mimeType = file?.mimeType || file?.type || file?.contentType;
  const fileName = file?.originalName || file?.fileName || file?.name || "";
  const extension = String(fileName).includes(".")
    ? String(fileName).split(".").pop()
    : "";

  return (
    extension ||
    String(mimeType || messageType || "file")
      .split("/")
      .pop()
  );
}

function getMessageDateLabel(message) {
  const date = message?.createdAt || message?.sentAt || message?.timestamp;

  if (!date || !moment(date).isValid()) return "";
  if (moment(date).isSame(moment(), "day")) return "Today";
  if (moment(date).isSame(moment().subtract(1, "day"), "day"))
    return "Yesterday";

  return moment(date).format("DD MMMM");
}

function getMessageTime(message) {
  const date = message?.createdAt || message?.sentAt || message?.timestamp;
  return date && moment(date).isValid() ? moment(date).format("HH:mm") : "";
}

function getRawMessageId(message) {
  return message?._id || message?.id;
}

function getMessageId(message, index) {
  return (
    getRawMessageId(message) || `${message?.createdAt || "message"}-${index}`
  );
}

function getMessageRoomId(message) {
  return (
    message?.room?._id ||
    message?.roomId?._id ||
    message?.roomId ||
    message?.chatRoomId ||
    message?.chatRoom?._id
  );
}

function getRoomTitle(room) {
  if (!room) return "Select conversation";

  return room?.order?.productDetails?.[0]?.name || "Conversation";
}

// function getRoomName(room) {
//   if (!room) return "Select conversation";
//   return room?.user?.fullName || "Conversation";
// }

function getRoomSubtitle(room) {
  if (!room) return "Choose a room from the left side";

  return (
    room?.admin?.email ||
    room?.order?.trackingId ||
    room?.order?.status ||
    room?.otherUser?.role ||
    room?.user?.role ||
    room?.roomType ||
    (room?.createdAt
      ? moment(room.createdAt).format("DD/MM/YYYY")
      : "Conversation")
  );
}

function getRoomName(room) {
  if (!room) return "Select conversation";
  return room?.user?.fullName || "Conversation";
}

function getRoomTime(room) {
  const date = room?.lastMessageAt || room?.updatedAt || room?.createdAt;

  return date && moment(date).isValid()
    ? moment(date).format("DD/MM/YYYY")
    : "";
}

function getOrderRouteId(room) {
  return (
    room?.order?._id ||
    room?.order?.id ||
    room?.orderId ||
    room?.order?.orderId ||
    ""
  );
}

function getOrderId(room) {
  if (!room) return "Select conversation";
  return room?.order?.orderId || "Order id";
}

function getLastMessagePreview(room) {
  if (typeof room?.lastMessage === "string") return room.lastMessage;

  return (
    room?.lastMessage?.message ||
    room?.lastMessage?.text ||
    room?.lastMessage?.content ||
    "No messages yet"
  );
}

function getInitials(name = "") {
  const initials = String(name)
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return initials || "NA";
}

function getCurrentUserId() {
  if (typeof window === "undefined") return "";

  const token = localStorage.getItem("token");
  const payload = decodeJwtPayload(token);

  return (
    payload?.id || payload?._id || payload?.userId || payload?.partnerId || ""
  );
}

function decodeJwtPayload(token) {
  if (!token || !token.includes(".")) return null;

  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );

    return JSON.parse(json);
  } catch (error) {
    return null;
  }
}

function Avatar({ initials, color, online = false, size = "default" }) {
  return (
    <span
      className={`messagesAvatar ${size === "large" ? "large" : ""}`}
      style={{ "--avatar-color": color }}
      aria-hidden="true"
    >
      {initials}
      {online && <span className="messagesPresence" />}
    </span>
  );
}

function MessageMeta({ time, light = false }) {
  return (
    <span className={`messagesMeta ${light ? "light" : ""}`}>
      {time}
      <CheckIcon />
    </span>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m21 21-4.2-4.2m1.2-5.3a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="M14 3H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7l-4-4Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="M14 3v4h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function AttachmentIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m21.4 11.6-8.7 8.7a6 6 0 0 1-8.5-8.5l9.1-9.1a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 1 1-2.8-2.8l8.6-8.6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m22 2-7 20-4-9-9-4 20-7Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="M22 2 11 13"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m4 12.5 3.5 3.5L16 7.5M13 15.5l1.5 1.5L23 8.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m15 18-6-6 6-6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="m9 18 6-6-6-6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="m4 17 4.4-4.4a2 2 0 0 1 2.8 0L15 16.4l1.4-1.4a2 2 0 0 1 2.8 0L21 16.8M15 9h.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path d="M0 0h24v24H0z" fill="none" />
      <g fill="none">
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M7.792 21.25h8.416a3.5 3.5 0 0 0 3.5-3.5v-5.53a3.5 3.5 0 0 0-1.024-2.475l-5.969-5.97A3.5 3.5 0 0 0 10.24 2.75H7.792a3.5 3.5 0 0 0-3.5 3.5v11.5a3.5 3.5 0 0 0 3.5 3.5"
        />
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M10.437 7.141c-.239.078-.392.236-.436.411c-.09.352 0 .73.253 1.203c.126.234.28.471.45.725l.092.137l.145.215l.019-.068l.086-.306q.148-.503.23-1.02c.089-.642-.011-1.018-.309-1.26c-.08-.065-.278-.119-.53-.037m.055 4.152l-.27-.362l-.032-.048c-.115-.19-.243-.38-.382-.585l-.1-.149a10 10 0 0 1-.512-.828c-.31-.578-.558-1.286-.358-2.067c.17-.664.698-1.081 1.227-1.254c.517-.168 1.174-.147 1.66.247c.792.644.848 1.573.739 2.357a9 9 0 0 1-.261 1.174l-.096.34q-.112.382-.208.769l-.067.194l1.392 1.864c.65-.078 1.364-.125 2.03-.077c.769.054 1.595.242 2.158.776a1.56 1.56 0 0 1 .395 1.441c-.117.48-.454.88-.919 1.123c-.985.515-1.902.105-2.583-.416c-.533-.407-1.045-.975-1.476-1.453l-.104-.114c-.37.057-.72.121-1.004.175c-.305.057-.684.128-1.096.22l-.151.443q-.125.288-.238.58l-.122.303a8 8 0 0 1-.427.91c-.33.578-.857 1.192-1.741 1.241c-1.184.066-1.986-.985-1.756-2.108l.006-.027c.2-.791.894-1.31 1.565-1.653c.597-.306 1.294-.532 1.941-.701zm.87 1.165l-.287.843l.421-.08l.004-.001l.38-.07zm2.84 1.604c.274.29.547.56.831.777c.55.42.94.493 1.299.305c.2-.105.284-.241.309-.342a.35.35 0 0 0-.08-.309c-.257-.228-.722-.38-1.392-.428a8 8 0 0 0-.967-.003m-5.005.947c-.318.109-.62.23-.89.368c-.587.3-.87.604-.944.867c-.078.415.192.673.516.655c.27-.015.506-.184.766-.639q.204-.372.358-.767l.107-.266z"
          clip-rule="evenodd"
        />
      </g>
    </svg>
  );
}
